import userInfoComponent from "./userInfoComponent";
import NavigationWrapper from "./navigationWrapper";
import PaymentDetailBodyComponent from "./paymentDetailBodyComponent";
import HeaderComponent from "./headerComponent";
import TradeInBodyComponent from "./tradeInBodyComponent";
import RebatesBodyComponent from "./rebatesBodyComponent";
import ItemizedPriceComponent from './itemizedPriceComponent';
import OffersBodyComponent from "./offersBodyComponent";
import MultiActionForms from './multiactionForms';
import fetchRequest from "./fetchRequest";
import Cookies from "js-cookie";
import _ from 'lodash';
import Toast from "./toastComponent";
import { popPageName } from '../../../helpers/osTracking';

/*eslint-disable camelcase*/
class OnlineShopperV2 {
    constructor(OS_DATA, OS_MODAL, emitter) {
        this.OS_DATA = OS_DATA;
        this.OS_MODAL = OS_MODAL;
        this.emitter = emitter;
        this.rebatesTab = OS_MODAL.querySelector('.payment-details__nav-containers.rebates');
        this.leaseTab = OS_MODAL.querySelector('.payment-details__options .lease');
        this.paymentsSettings;
        this.fetchParams = {};
        this.vehicleState = {};
        this.dealData;
        this.personData = {};
        this._loadTypeAhead();
        this._handleUsedVehicle();
        this.zipMaxLength = 5;

        this.getChangedValues = this.getChangedValues.bind(this);
        this._dealRevisionInterval = this._dealRevisionInterval.bind(this);
        this._stopDealRevision = this._stopDealRevision.bind(this);
        this._checkOffers = this._checkOffers.bind(this);
        this._zipCodeCheck = this._zipCodeCheck.bind(this);
        this._isDealerZip = this._isDealerZip.bind(this);

        emitter.on('user-changed-value', (event, optionChanged) => {
            this.getChangedValues(event, optionChanged);

            if (!this.updatePaymentsInterval) {
                this._checkDealRevisions();
            }
        });

        emitter.on('user-updated-trade-in', () => {
            if (!this.updatePaymentsInterval) {
                this._checkDealRevisions();
            }
        });

        this.closeButton = this.OS_MODAL.querySelector('[name="close"]');
        if (this.closeButton) {
            this.closeButton.addEventListener("click", this._stopDealRevision);
        }

        this.OS_MODAL.addEventListener('click', (e) => {
            if (this.OS_MODAL.childElementCount < 1) {
                popPageName();
            }
        });
    }

    async init(monthlyPayment) {
        this.monthlyPayment = monthlyPayment;

        this.userInfoComponent = new userInfoComponent(this.OS_DATA, this.OS_MODAL, this.emitter);
        this._registerComponents();

        if (this.OS_DATA.onlineShopperV2 && this.OS_DATA.onlineShopperSlug) {
            this.fetchParams.apiBase = this.OS_DATA.onlineShopperApiBase;
            this.fetchParams.accountSlug = this.OS_DATA.onlineShopperSlug;

            this.paymentsSettings = await fetchRequest.getAccountPaymentsSettings(this.fetchParams);
            const { dealData, existingDeal } = this._getDeal();
            this.dealData = await dealData;
            this.vehicleState = this.dealData.vehicles[0];

            if (this.dealData.person) {
                this.personData = this.dealData.person;
            }

            this._setFetchParams(this.dealData, existingDeal);

            this._checkDealRevisions();
            this._initComponentsWithApiData();
            this._showHideLease(this.vehicleState.payments.lease.enabled);

            const expDays = 7;
            const cookieName = `OnlineShopper${this.OS_DATA.vin}`;
            Cookies.set(cookieName, this.fetchParams, { expires: expDays });
        }
    }

    _getDeal() {
        const dealCookie = `OnlineShopper${this.OS_DATA.vin}`;
        const existingDeal = Cookies.getJSON(dealCookie);
        let dealData;
        if (this.OS_DATA.vin && !existingDeal) {
            //use new deal
            dealData = this._createDeal(this.OS_DATA);
        } else {
            //use existing deal
            dealData = fetchRequest.getDealRevisions(existingDeal, true);
        }
        return { dealData, existingDeal };
    }

    _setFetchParams(dealData, existingDeal) {
        if (existingDeal) {
            this.fetchParams = existingDeal;
        } else {
            this.fetchParams.uuid = dealData.uuid;
            this.fetchParams.api_key = dealData.api_key;
            this.fetchParams.current_data_revision = dealData.current_data_revision;
        }
    }

    _registerComponents() {
        const paymentDetailBodyContainer = this.OS_MODAL.querySelector('.payment-details__body-container');
        this.PaymentDetailBodyComponent = new PaymentDetailBodyComponent(paymentDetailBodyContainer, this.emitter);

        const tradeInContainer = this.OS_MODAL.querySelector(".trade-in__body-container");
        this.TradeInBodyComponent = new TradeInBodyComponent(tradeInContainer, this.OS_MODAL, this.emitter);

        this.NavigationWrapper = new NavigationWrapper(this.OS_MODAL, this.OS_DATA.used, this.emitter);

        const itemizedPriceContainer = this.OS_MODAL.querySelector('.payment-details__itemized-container');
        this.ItemizedPriceComponent = new ItemizedPriceComponent(itemizedPriceContainer, this.OS_MODAL, this.emitter);

        const headerContainer = this.OS_MODAL.querySelector('.payment-details__header-container');
        this.HeaderComponent = new HeaderComponent(this.OS_DATA, headerContainer, this.emitter, this.monthlyPayment);

        this.MultiActionForms = new MultiActionForms(this.OS_DATA.vin, this.OS_MODAL, this.emitter);

        const rebatesBodyContainer = this.OS_MODAL.querySelector('.rebates__body');
        this.RebatesBodyComponent = new RebatesBodyComponent(rebatesBodyContainer, this.emitter);

        const offersBodyContainer = this.OS_MODAL.querySelector('.payment-options__body');
        this.OffersBodyComponent = new OffersBodyComponent(this.OS_DATA, offersBodyContainer, this.emitter);
    }

    _initComponentsWithApiData() {
        this.PaymentDetailBodyComponent.init(this.paymentsSettings, this.dealData, this.personData);
        this.TradeInBodyComponent.init(this.fetchParams);
        this.NavigationWrapper.init(this.vehicleState);
        this.HeaderComponent.update({ ...this.vehicleState }, this.personData, this.paymentsSettings);
        this.MultiActionForms.init(this.fetchParams, this.personData, this.vehicleState);
        this.RebatesBodyComponent.init(this.vehicleState);
        this.OffersBodyComponent.init({ ...this.vehicleState }, this.fetchParams, this.personData);
    }

    _createDeal(data) {
        const requestBody = {
            account_slug: data.onlineShopperSlug,
            vins: [data.vin]
        };

        return fetchRequest.createNewDeal(this.fetchParams, requestBody);
    }

    async _dealRevisionInterval() {
        if (document.visibilityState === "visible") {
            const dealRevision = await fetchRequest.getDealRevisions(this.fetchParams);
            if (this.fetchParams.current_data_revision < dealRevision.current_data_revision) {
                this.fetchParams.current_data_revision = dealRevision.current_data_revision;
                const expDays = 7;
                const cookieName = `OnlineShopper${this.OS_DATA.vin}`;

                Cookies.set(cookieName, this.fetchParams, { expires: expDays });

                //update things
                this.vehicleState = dealRevision.vehicles[0];
                this.selected = this.vehicleState.payments.selected;
                await this._zipCodeCheck();
                this.emitter.emit('new-vehicle-state', this.vehicleState, this.personData);
                this.emitter.emit('new-vdp-monthly-payment', dealRevision, this.OS_DATA.used);
            }
            this._checkOffers();
        }
    }

    _checkDealRevisions() {
        if (this.fetchParams && this.fetchParams.uuid && this.fetchParams.api_key) {
            this.updatePaymentsInterval = setInterval(this._dealRevisionInterval, 1500);
        }
    }

    _stopDealRevision() {
        clearInterval(this.updatePaymentsInterval);
        this.updatePaymentsInterval = null;
    }

    _checkOffers() {
        const payments = this.vehicleState.payments;
        let isLoanDone = false;
        let isLeaseDone = false;
        let isCashDone = false;
        if (payments.loan && payments.lease && payments.cash) {
            isLoanDone = payments.loan.data_hash ? payments.loan.data_hash === payments.loan.request_hash : false;

            isLeaseDone = payments.lease.enabled ?
                payments.lease.data_hash ?
                    payments.lease.data_hash === payments.lease.request_hash
                    : false
                : true;
            isCashDone = payments.cash.data_hash ? payments.cash.data_hash === payments.cash.request_hash : false;
        }

        if (isLoanDone && isLeaseDone && isCashDone) {
            this._stopDealRevision();
        } else {
            return;
        }
    }

    /* istanbul ignore next */
    async getChangedValues(event, optionChanged) {
        const requestBody = {};
        let callPatchPayment = false;
        const value = parseInt((event.target.value).replace(/\D/g, ''), 10);
        this.HeaderComponent._displayLoadAnimation();
        if (optionChanged === 'payment-option') {
            requestBody.selected = event.target.value;
            callPatchPayment = true;
        } else if (optionChanged === 'term') {
            requestBody[this.vehicleState.payments.selected] = {
                term: value
            };
            callPatchPayment = true;
        } else if (optionChanged === 'miles') {
            requestBody[this.vehicleState.payments.selected] = {
                miles: value
            };
            callPatchPayment = true;
        } else if (optionChanged === 'down-payment') {
            requestBody[this.vehicleState.payments.selected] = {
                down_payment: value
            };
            callPatchPayment = true;
        } else if (optionChanged === 'credit-rating') {
            this.personData = await fetchRequest.patchPerson(this.fetchParams, { credit_estimate: value });
            this.OffersBodyComponent.updateCreditRating(this.personData);
            this._updatePersonDataCookie();
        } else if (optionChanged === 'zip-input') {
            if (event.target.value.length === this.zipMaxLength) {
                this.personData = await fetchRequest.patchPerson(this.fetchParams, { zip: event.target.value });
                this._updatePersonDataCookie();
            }
        }

        if (callPatchPayment) {
            this.vehicleState.payments = await fetchRequest.patchPayment(this.fetchParams, this.vehicleState.vin, requestBody);
            this.emitter.emit('new-vehicle-state', this.vehicleState, this.personData);
        }
    }

    async _zipCodeCheck() {
        if (this._isDealerZip()) {
            return;
        }

        const loanTaxes = _.get(this.vehicleState, "payments.loan.offers[0].taxes", []);
        const cashTaxes = _.get(this.vehicleState, "payments.cash.offers[0].taxes", []);

        if (loanTaxes.length === 0 && cashTaxes.length === 0) {
            Toast.show("warning", "Oops, Sorry!", "We're not able to calculate your taxes and fees based on the ZIP code provided. Please enter a valid ZIP code or contact the seller.", 5000, false, true);
            this.personData = await fetchRequest.patchPerson(this.fetchParams, {zip: this.OS_DATA.dealerZip});
            this.emitter.emit('new-vehicle-state', this.vehicleState, this.personData);
            this.PaymentDetailBodyComponent.personData = {...this.personData};
            this.PaymentDetailBodyComponent.setZipCode();
            this._updatePersonDataCookie();

            if (!this.updatePaymentsInterval) {
                this._checkDealRevisions();
            }
        }
    }

    _isDealerZip() {
        return !this.personData.zip || this.OS_DATA.dealerZip === this.personData.zip;
    }

    _updatePersonDataCookie() {
        const expDays = 7;
        const cookieName = `ElectricLeadSent${this.OS_DATA.vin}`;

        Cookies.set(cookieName, this.personData, { expires: expDays });
    }

    _loadTypeAhead() {
        const isLoaded = document.querySelectorAll('.type-ahead');
        if (isLoaded.length > 0) {
            return;
        }

        /*istanbul ignore next*/
        const { jquery, typeAheadTwitter, typeAheadSNAP } = this._createTypeAheadSripts();

        /* istanbul ignore next*/
        if (document.body.append) {
            document.body.append(jquery);
            jquery.onload = jquery.onreadystatechange = function () {
                document.body.append(typeAheadTwitter, typeAheadSNAP);
            };
        } else {
            document.body.appendChild(jquery);
            jquery.onload = jquery.onreadystatechange = function () {
                document.body.appendChild(typeAheadTwitter);
                document.body.appendChild(typeAheadSNAP);
            };
        }
    }

    _handleUsedVehicle() {
        if (this.OS_DATA.used) {
            this.rebatesTab.classList.add('hidden');
        }
    }

    _showHideLease(leaseEnabled) {
        const localLeaseTab = this.OS_MODAL.querySelector('.payment-details__options .lease');
        if (leaseEnabled) {
            localLeaseTab.classList.remove('hidden');
        }
    }

    _createTypeAheadSripts() {
        const jquery = document.createElement("script");
        jquery.src = "https://code.jquery.com/jquery-1.12.0.min.js";
        jquery.className = "type-ahead jquery";

        const typeAheadTwitter = document.createElement("script");
        typeAheadTwitter.src = "https://cdnjs.tradepending.com/javascript/typeahead.js";
        typeAheadTwitter.className = "type-ahead twitter";

        const typeAheadSNAP = document.createElement("script");
        typeAheadSNAP.src = "https://cdnjs.tradepending.com/javascript/snap-typeahead-v3.js";
        typeAheadSNAP.className = "type-ahead snap";

        return { jquery, typeAheadTwitter, typeAheadSNAP };
    }
}


export default OnlineShopperV2;
